import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Bank, BankAccount, Transaction } from "./types";
import { EmptyState } from "./components/EmptyState";
import { Dashboard } from "./components/Dashboard";
import {
  mockBankAccounts,
  mockBankAccountsEmpty,
  mockTransactions,
  mockReviewSuggestions,
} from "./mockData";

export function BankTransactionsHeader() {
  const navigate = useNavigate();
  return (
    <div className="px-4 py-3.5 md:px-8">
      <div className="text-[12.5px] text-ink3 mb-1 font-medium">
        <span className="cursor-pointer" onClick={() => navigate("/accounting")}>
          Accounting
        </span>
        <span className="text-ink3 mx-1.5">/</span>
        <span>Bank transactions</span>
      </div>
      <h1 className="text-xl md:text-3xl font-extrabold m-0 -tracking-[0.3px] text-ink">
        Bank transactions
      </h1>
      <div className="text-[12.5px] md:text-[13.5px] text-ink3 mt-1.5 font-medium">
        Cleaned descriptions, matched invoices and payroll runs, categorized automatically.
      </div>
    </div>
  );
}

function getInitialState(): { accounts: BankAccount[]; transactions: Transaction[] } {
  if (typeof window === "undefined") {
    return { accounts: mockBankAccounts, transactions: mockTransactions };
  }
  const params = new URLSearchParams(window.location.search);
  const forceEmpty =
    params.get("empty") === "1" ||
    params.get("populated") === "0" ||
    params.get("demo") === "empty";
  if (forceEmpty) {
    return { accounts: mockBankAccountsEmpty, transactions: [] };
  }
  return { accounts: mockBankAccounts, transactions: mockTransactions };
}

function newAccountFromBank(bank: Bank): BankAccount {
  const suffix = String(1000 + Math.floor(Math.random() * 9000));
  return {
    id: `acc_${Date.now()}`,
    bankCode: bank.code ?? "connected",
    bankName: bank.name ?? "Connected bank",
    accountName: "Business Chequing",
    accountNumberMasked: `••${suffix}`,
    balance: Math.round((5000 + Math.random() * 15000) * 100) / 100,
    lastSyncedAt: new Date().toISOString(),
  };
}

export default function BankTransactionsPage() {
  const initial = getInitialState();
  const [accounts, setAccounts] = useState<BankAccount[]>(initial.accounts);
  const [transactions, setTransactions] = useState<Transaction[]>(initial.transactions);

  const handleBankConnected = (bank: Bank) => {
    const newAcc = newAccountFromBank(bank);
    setAccounts((prev) => [...prev, newAcc]);
    if (transactions.length === 0) {
      setTransactions(mockTransactions);
    }
  };

  const isEmpty = accounts.length === 0;

  return (
    <div className="max-w-full m-0 -mt-7 px-4 pt-5 pb-24 bg-white md:px-8">
      {isEmpty ? (
        <EmptyState onBankConnected={handleBankConnected} />
      ) : (
        <Dashboard
          accounts={accounts}
          transactions={transactions}
          reviewSuggestions={mockReviewSuggestions}
          onBankConnected={handleBankConnected}
        />
      )}
    </div>
  );
}